// Core
import { useState } from 'react';
import { 
  Box,
  Button,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  useColorMode,
  useDisclosure
} from '@chakra-ui/react';
import { useQuery } from 'react-query';

// Assets
import logoImage from 'assets/img/logo-3d.png';
import { apiService, ApiError, getApiError } from 'services/ApiService';

// Components
import { BreadcrumbArrowIcon } from 'components/Icons/Icons';
import ErrorCard from 'components/Form/Errors';
import PendingDeletion from './PendingDeletion';
import ContinueSaving from './ContinueSaving';
import ReasonForCancelling from './ReasonForCancelling';
import StatesModal from './modals/StatesModal';
import { useCancellationAttempt } from './useCancellationAttempt';

const CancelSubscription = ({ 
  userData, 
  isOpen, 
  onClose, 
  onOpen, 
  onCancelled,
  changeStatusSubItem,
  subscriptionId,
  entryPoint
}) => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';

  const { isOpen: isOpenPreNextStep, onClose: onClosePreNextStep, onOpen: onOpenPreNextStep } = useDisclosure();

  const [step, setStep] = useState(1);
  const [reasonRadio, setReasonRadio] = useState();
  const [reasonRadioOther, setReasonOtherRadio] = useState();
  const [buttonReasonAndCancelMembership, setButtonReasonAndCancelMembership] = useState(false);
  const [cancelError, setCancelError] = useState();

  const { markRetained, markConfirmed, leaveForPortal, knownAttemptId } = useCancellationAttempt({
    isOpen, subscriptionId, entryPoint, step
  });

  const resetFlow = () => {
    setStep(1);
    setReasonRadio();
    setReasonOtherRadio();
    setCancelError();
    setButtonReasonAndCancelMembership(false);
  };

  const handleClose = () => {
    resetFlow();
    onClose();
  };

  const scrollToTop = () => {
    const topEl = document.getElementById('modalTop');
    topEl?.scrollIntoView({ behavior: 'auto', block: 'start' });
  };

  const backButton = async () => {
    if(step === 1){
      handleClose();
    } else if(step === 2){
      setStep(1);
    } else if(step === 3){
      setStep(2);
    }

    scrollToTop();
    setCancelError(undefined);
    setButtonReasonAndCancelMembership(false);
  };

  const nextButton = async () => {
    if(step === 1){
      setStep(2);
    } else if(step === 2 && !isOpenPreNextStep){
      onOpenPreNextStep();
    } else if(step === 2 && isOpenPreNextStep){
      onClosePreNextStep();
      setStep(3);
    }

    scrollToTop();
    setCancelError(undefined);
    setButtonReasonAndCancelMembership(false);
  };

  const buildReason = () => {
    if (reasonRadio !== 'Other') {
      return reasonRadio;
    }
    const otherReason = (reasonRadioOther ?? '').trim();
    return otherReason.length ? otherReason : 'Other';
  };

  const sendReasonAndCancelMembership = async () => {
    setCancelError(undefined);
    setButtonReasonAndCancelMembership(true);
    try {
      const attemptId = await knownAttemptId();
      const response = await apiService.unSubscriptionWithReason({
        id: subscriptionId,
        reason: buildReason(),
        attemptId
      });

      const apiError = getApiError(response);
      if (apiError) {
        throw apiError;
      }

      markConfirmed();
      onCancelled();
    } catch (error) {
      console.error('Error:', error);
      // Show the server's own message only for errors the server returned;
      // network failures and 5xx get the generic ErrorCard text.
      setCancelError(error instanceof ApiError ? error.message : true);
      setButtonReasonAndCancelMembership(false);
    }
  };

  const { data: activePlans, isLoading: isLoadingPlans } = useQuery({
      queryKey: ['active-plans'],
      queryFn: async () => {
          const response = await apiService.getSubscriptions({status: 'active'});
          return response.data;
      },
      refetchOnWindowFocus: false
  });

  // Entries and coins are account-wide. They are only lost when the plan being
  // cancelled is the member's last live plan, so check whether another active or
  // trialing plan remains rather than counting active plans (the cancelled plan
  // itself may be past_due and not in this list).
  // Until the plan list and the profile (which gives subscriptionId on the
  // membership page) have both loaded the answer is unknown: undefined, so the
  // steps show neither wording instead of one that may be swapped a moment later.
  const keepsAnotherPlan = isLoadingPlans || !userData
    ? undefined
    : !!activePlans?.some((plan) => plan.subscriptionId !== subscriptionId);

  return (
  	<> 
  	<Modal isOpen={isOpen} onClose={handleClose}>
      <ModalOverlay />
      <ModalContent 
      	className="modal__simple modal__simple_full-screen" 
        style={{borderRadius: '0 !important'}}>
        <ModalBody>
          <Box id="modalTop" mb="40px" />

          <Button 
            variant="secondary-md" 
            w="118px" 
            onClick={backButton}
            leftIcon={
              <BreadcrumbArrowIcon 
                fontSize="24px" 
                style={{
                  transform: 'rotate(180deg)',
                  marginLeft: '-12px'
                }}
                />
            }
          >Back</Button>

          <Image src={logoImage} 
            m={{ base: '32px auto 24px auto', md: '12px auto 48px auto'}}
            w={{ base: '154px', md: '268px'}} 
            h={{ base: '30px', md: '52px'}} />

          { step === 1 && (
            <PendingDeletion
              userData={userData}
              isDark={isDark}
              keepsAnotherPlan={keepsAnotherPlan} />
          )}

          { step === 2 && (
            <ContinueSaving 
              userData={userData} 
              isDark={isDark} 
              onClose={handleClose} 
              onRetained={markRetained}
              setStep={setStep}
              changeStatusSubItem={changeStatusSubItem}
              />
          )}

          <StatesModal
            isOpen={isOpenPreNextStep}
            onClose={onClosePreNextStep}
            onOpen={onOpenPreNextStep}
            isDark={isDark}
            changeStatusSubItem={changeStatusSubItem}
            nextButton={nextButton}
            onRetained={markRetained}
            onLeaveForPortal={leaveForPortal}
            userData={userData}
            />

          { step === 3 && (
            <ReasonForCancelling 
              userData={userData} isDark={isDark}
              reasonRadio={reasonRadio}
              setReasonRadio={setReasonRadio}
              reasonRadioOther={reasonRadioOther}
              setReasonOtherRadio={setReasonOtherRadio}
              changeStatusSubItem={changeStatusSubItem}
              keepsAnotherPlan={keepsAnotherPlan}
              />
          )}

          <Box m="0 auto" maxW="346px" w="100%">
            { step === 3 && cancelError && (
              <ErrorCard mt="64px" mb="-52px" error={cancelError} />
            )}
            <Button mt="64px" onClick={handleClose} variant={ isDark ? 'button-lg-g' : 'button-lg'}>I don`t want to cancel, Return to profile</Button>
            { step !== 3 ?
              <Button mt="12px" onClick={nextButton} isDisabled={step === 1 && keepsAnotherPlan === undefined} variant="delete-md">Proceed with Cancelling Plan</Button>
            :
              <Button mt="12px" 
                variant="delete-md" 
                disabled={!reasonRadio}
                isLoading={buttonReasonAndCancelMembership}
                onClick={sendReasonAndCancelMembership}>Confirm Plan Cancellation</Button>
            }
          </Box>

          <Box h="80px" />
        </ModalBody>
      </ModalContent>
    </Modal> 
    </>
  );
};

export default CancelSubscription;